import { useRef, useState } from 'react';
import axios from 'axios';
import ActiveTasksContext from './ActiveTasksContext';
import { playNotifySound, playErrorSound } from '../utils/notifySound';

const TASK_AUTO_REMOVE_MS = 20000;
const TOAST_AUTO_DISMISS_MS = 5000;

let taskCounter = 0;
const nextId = () => 'task-' + Date.now() + '-' + (taskCounter++);

// App-root-level home for "things the system is doing in the background
// that the teacher might want to check on" - mounted once in App.js next to
// PendingApprovalsProvider. The key property this buys: an in-flight axios
// request started here lives in THIS component's closure, not the modal
// that triggered it, so it keeps running (and its .then()/.catch() still
// fire) no matter what the teacher navigates to or closes in the meantime -
// there is nothing more to "resume", the promise was never tied to the
// requesting component's lifecycle in the first place.
//
// Today this only knows how to run one task type (quiz generation, a single
// synchronous POST) - the task/toast list shape is intentionally generic
// (id/type/status/label/...) so a future WS-sourced task (e.g. a long cron
// job with no single owning request) can be pushed into the same `tasks`
// array without reshaping this provider.
export default function ActiveTasksProvider({ children }) {
   const [tasks, setTasks] = useState([]);
   const [toasts, setToasts] = useState([]);
   const timersRef = useRef({});

   const pushToast = (toast) => {
      const id = nextId();
      setToasts((prev) => [...prev, { id, ...toast }]);
      timersRef.current[id] = setTimeout(() => dismissToast(id), TOAST_AUTO_DISMISS_MS);
   };

   const dismissToast = (id) => {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
      setToasts((prev) => prev.filter((t) => t.id !== id));
   };

   const dismissTask = (id) => {
      clearTimeout(timersRef.current['task-' + id]);
      delete timersRef.current['task-' + id];
      setTasks((prev) => prev.filter((t) => t.id !== id));
   };

   const updateTask = (id, patch) => {
      setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
   };

   const scheduleAutoRemove = (id) => {
      timersRef.current['task-' + id] = setTimeout(() => dismissTask(id), TASK_AUTO_REMOVE_MS);
   };

   const startQuizGenerationTask = ({ studentId, studentLabel, request }) => {
      const id = nextId();
      setTasks((prev) => [...prev, {
         id, type: 'quiz-generation', status: 'running',
         studentId, studentLabel, conversationId: null, conversationName: null, errorMessage: null,
         startedAt: Date.now(),
      }]);

      axios.post('/api/quiz-generation/generate', JSON.stringify(request),
         { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
         .then((response) => {
            const conversation = response.data && response.data.conversationId;
            updateTask(id, {
               status: 'success',
               conversationId: conversation ? conversation.id : null,
               conversationName: conversation ? conversation.name : null,
            });
            playNotifySound();
            pushToast({ taskType: 'quiz-generation', status: 'success', studentLabel, conversationName: conversation ? conversation.name : null });
            scheduleAutoRemove(id);
         })
         .catch((err) => {
            const message = err.response && typeof err.response.data === 'string' ? err.response.data : null;
            updateTask(id, { status: 'error', errorMessage: message });
            playErrorSound();
            pushToast({ taskType: 'quiz-generation', status: 'error', studentLabel, errorMessage: message });
            scheduleAutoRemove(id);
         });

      return id;
   };

   return (
      <ActiveTasksContext.Provider value={{ tasks, toasts, startQuizGenerationTask, dismissTask, dismissToast }}>
         {children}
      </ActiveTasksContext.Provider>
   );
}
